import { useParams, useNavigate } from "react-router";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";

// Store
// import useBrainStore from "../store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";

// Utils
import { detectBrowserAndOS } from "@/utils/browserDetect";
import { MOBILE_BREAKPOINT_PX } from "@/utils/breakpoints";

// Components
import Hero from "./Hero/Hero";
import HeroSubtitle from "./Hero/HeroSubtitle";
// main
import Organism from "./Organism/Organism";
import NeurodynamicsTabGroup from "./Neurodynamics/NeurodynamicsTabGroup";
import ConnectomicsTabGroup from "./Connectomics/ConnectomicsTabGroup";
import CompNeuro from "./CompNeuro/CompNeuro";
import Projections from "./Projections/Projections";
import Summary from "./Summary/Summary";
import Breakdown from "./Breakdown/Breakdown";
import SponsorBanner from "./SponsorBanner/SponsorBanner";
// main end
import Footer from "@/components/Footer/Footer";

// Sentry test component
// import SentryTest from "../components/_common/SentryTest";

// --------------------------------------------------------------------------------------------

export default function Home({ isLinkMode }) {
	// Check for iOS only once on component mount, outside of useEffect
	const { isIos, isAndroid } = detectBrowserAndOS();

	const isMobile = useMediaQuery({
		query: `(max-width: ${MOBILE_BREAKPOINT_PX}px)`,
	});

	// --------------------------------------------------------------------------------------------

	// --- FETCH SCENARIO from KV ---
	// get id from URL params, to match with KV store
	const { id } = useParams();

	// redirect to home if link doesn't exist
	// this is just a prep for later (below)
	const navigate = useNavigate();

	// Subscribe to id changes
	// so that if user on working url changes to non-working url, we redirect instead of staying
	const fetchedId = useLinkStore((s) => s.fetchedId);

	useEffect(() => {
		if (isLinkMode && id && fetchedId !== id) {
			fetch(`/api/get-scenario-by-id/${id}`)
				.then(
					(res) =>
						res.ok
							? res.json() // If res.ok is true, parse JSON and continue
							: res.json().then((body) => Promise.reject(body.error)), // If res.ok is false, parse the error from JSON and reject
				)

				.then((data) => {
					// Overwrite the linkStore with the JSON from KV
					useLinkStore.setState((prev) => ({
						...prev,
						fetchedId: id,
						originalData: data, // store json data for Reset btn functionality on link page
						...data, // set current 'link' state
					}));
				})

				// catch; receive err from Promise.reject
				.catch((err) => {
					console.error("Error loading link scenario:", err);
					// Show toast error message and redirect to home
					toast.error("Link doesn't exist. Redirected to homepage.");
					navigate("/", { replace: true });
				});
		}
	}, [isLinkMode, id, fetchedId, navigate]);

	// --------------------------------------------------------------------------------------------

	// Due to no respect for svh on iphone - arc, firefox, and ipad - chrome, arc, firefox
	useEffect(() => {
		// Function to set the viewport height custom property
		function setStableHeight() {
			document.documentElement.style.setProperty(
				"--vh",
				`${window.innerHeight}px`,
			);
		}

		// Set height after a short delay on mount to allow toolbars to potentially settle
		// w/out the timeout, it doesn't work, don't comment it out. 1ms is enough
		const timerId = setTimeout(setStableHeight, 1);

		// on desktop, listen for resize, so that we can do responsive development
		if (!isIos && !isAndroid) {
			window.addEventListener("resize", setStableHeight);
		}

		// Always listen for orientation changes to recalculate
		window.addEventListener("orientationchange", setStableHeight);

		// Cleanup function
		return () => {
			clearTimeout(timerId);
			window.removeEventListener("resize", setStableHeight);
			window.removeEventListener("orientationchange", setStableHeight);
		};
	}, [isIos, isAndroid]); // Dependencies, though they won't change during component lifecycle

	// --------------------------------------------------------------------------------------------

	return (
		<>
			{/* Page head */}
			<title>BrainEm ￭ Calculator</title>
			<meta
				name="description"
				content="How much USD to emulate a brain? A companion calculator for The State of Brain Emulation Report 2025."
			/>

			{/* ------------------------------------------------------------------------------------ */}

			{/* <SentryTest /> */}
			<Hero />
			{/* On mobile, render HeroSubtitle here, below the 100vh Hero component */}
			{isMobile && <HeroSubtitle />}

			<main>
				<div className="split-wrapper">
					<div className="left-sections">
						<Organism isLinkMode={isLinkMode} />
						<NeurodynamicsTabGroup isLinkMode={isLinkMode} />
						<ConnectomicsTabGroup isLinkMode={isLinkMode} />
						<CompNeuro isLinkMode={isLinkMode} />
						<Projections isLinkMode={isLinkMode} />
					</div>
					{/* Right section */}
					<Summary isLinkMode={isLinkMode} />
				</div>
				<Breakdown />
				<SponsorBanner />
			</main>

			<Footer />
		</>
	);
}

// --------------------------------------------------------------------------------------------

Home.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
};
